/* -------------------------------------------------------------------------- */
/* ---------------------------- MAP : FRAMEWORKS ---------------------------- */

// FRAMEWORKS : NODES_MODULES
import "leaflet";
import "leaflet-gesture-handling";

/* -------------------------------------------------------------------------- */
/* --------------------------- MAP : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	initLeafletMap();
	initCityAutocomplete();
});

/* -------------------------------------------------------------------------- */
/* ------------------------- MAP : GLOBAL FUNCTIONS ------------------------- */

function geoloc() {
	if ("geolocation" in navigator) {
		navigator.geolocation.getCurrentPosition(
			function (position) {
				const userLatitude = position.coords.latitude;
				const userLongitude = position.coords.longitude;
				const url =
					window.location.protocol +
					"//" +
					window.location.host +
					window.location.pathname;
				window.location.href =
					url + "?lat=" + userLatitude + "&lon=" + userLongitude;
			},
			function (error) {
				console.error("Erreur de géolocalisation : " + error.message);
			}
		);
	} else {
		console.error(
			"La géolocalisation n'est pas prise en charge par ce navigateur."
		);
	}
}

// Cache des communes par département (évite de re-télécharger à chaque frappe)
const departmentCommunesCache = new Map();

// Déduit le(s) code(s) département possibles à partir d'un début de code postal
function getDepartmentCodesForPostcode(query) {
	const prefix2 = query.slice(0, 2);

	if (prefix2 === "97" || prefix2 === "98") {
		// DOM-TOM : le département fait 3 chiffres (971, 972, 974, ...)
		return query.length >= 3 ? [query.slice(0, 3)] : [];
	}

	if (prefix2 === "20") {
		// Corse : les codes postaux en 20xxx sont répartis entre 2A et 2B
		return ["2A", "2B"];
	}

	return [prefix2];
}

// Récupère (et met en cache) la liste des communes d'un département (nom + codes postaux)
async function fetchDepartmentCommunes(departmentCode, signal) {
	if (departmentCommunesCache.has(departmentCode)) {
		return departmentCommunesCache.get(departmentCode);
	}

	try {
		const response = await fetch(
			`https://geo.api.gouv.fr/departements/${departmentCode}/communes?fields=nom,codesPostaux`,
			{ signal }
		);
		const communes = response.ok ? await response.json() : [];
		departmentCommunesCache.set(departmentCode, communes);
		return communes;
	} catch (error) {
		if (error.name === "AbortError") throw error;
		departmentCommunesCache.set(departmentCode, []);
		return [];
	}
}

// Suggestions par préfixe de code postal (l'API de géocodage IGN ne recherche que par code exact)
async function fetchPostcodeSuggestions(query, signal) {
	const departmentCodes = getDepartmentCodesForPostcode(query);
	const communesLists = await Promise.all(
		departmentCodes.map((code) => fetchDepartmentCommunes(code, signal))
	);

	const results = [];
	communesLists.flat().forEach((commune) => {
		(commune.codesPostaux || []).forEach((postcode) => {
			if (postcode.startsWith(query)) {
				results.push({ city: commune.nom, postcode: postcode });
			}
		});
	});

	return results.sort((a, b) => a.postcode.localeCompare(b.postcode)).slice(0, 10);
}

// Suggestions par nom de ville (recherche floue de l'API de géocodage IGN)
async function fetchCitySuggestions(query, signal) {
	const params = new URLSearchParams({ type: "municipality", q: query, limit: 10 });
	const response = await fetch(`https://data.geopf.fr/geocodage/search?${params.toString()}`, {
		signal,
	});
	const data = await response.json();

	return (data["features"] || [])
		.map((feature) => feature["properties"])
		.filter((properties) => properties && properties["city"] && properties["postcode"]);
}

// Autocomplétion du champ "ville" (recherche par nom de ville ou code postal)
function initCityAutocomplete() {
	const input = document.querySelector('.form__control[name="ville"]');
	const suggestionsList = document.getElementById("ville-suggestions");

	if (!input || !suggestionsList) return;

	let debounceTimer = null;
	let abortController = null;
	let results = [];
	let activeIndex = -1;

	function closeSuggestions() {
		suggestionsList.innerHTML = "";
		suggestionsList.hidden = true;
		input.setAttribute("aria-expanded", "false");
		input.removeAttribute("aria-activedescendant");
		results = [];
		activeIndex = -1;
	}

	function selectSuggestion(result) {
		input.value = result.postcode;
		closeSuggestions();
		input.focus();
	}

	function updateActiveItem() {
		Array.from(suggestionsList.children).forEach((item, index) => {
			item.classList.toggle("--active", index === activeIndex);
		});
		if (activeIndex >= 0) {
			input.setAttribute("aria-activedescendant", `ville-suggestion-${activeIndex}`);
		} else {
			input.removeAttribute("aria-activedescendant");
		}
	}

	function renderSuggestions(newResults) {
		results = newResults;
		activeIndex = -1;
		suggestionsList.innerHTML = "";

		if (!results.length) {
			closeSuggestions();
			return;
		}

		results.forEach((result, index) => {
			const item = document.createElement("li");
			item.id = `ville-suggestion-${index}`;
			item.className = "form__suggestions__item";
			item.setAttribute("role", "option");
			item.textContent = `${result.city} (${result.postcode})`;
			// "mousedown" + preventDefault : évite que le "blur" de l'input ne ferme la liste avant le clic
			item.addEventListener("mousedown", (event) => {
				event.preventDefault();
				selectSuggestion(result);
			});
			suggestionsList.appendChild(item);
		});

		suggestionsList.hidden = false;
		input.setAttribute("aria-expanded", "true");
	}

	async function fetchSuggestions(query) {
		if (abortController) {
			abortController.abort();
		}
		abortController = new AbortController();

		try {
			const newResults = /^[0-9]+$/.test(query)
				? await fetchPostcodeSuggestions(query, abortController.signal)
				: await fetchCitySuggestions(query, abortController.signal);

			renderSuggestions(newResults);
		} catch (error) {
			if (error.name !== "AbortError") {
				console.error("Erreur d'autocomplétion : " + error.message);
			}
		}
	}

	input.addEventListener("input", () => {
		const query = input.value.trim();

		clearTimeout(debounceTimer);

		if (query.length < 3) {
			closeSuggestions();
			return;
		}

		debounceTimer = setTimeout(() => {
			fetchSuggestions(query);
		}, 300);
	});

	input.addEventListener("keydown", (event) => {
		if (suggestionsList.hidden || !results.length) return;

		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				activeIndex = (activeIndex + 1) % results.length;
				updateActiveItem();
				break;
			case "ArrowUp":
				event.preventDefault();
				activeIndex = (activeIndex - 1 + results.length) % results.length;
				updateActiveItem();
				break;
			case "Enter":
				if (activeIndex >= 0) {
					event.preventDefault();
					selectSuggestion(results[activeIndex]);
				}
				break;
			case "Escape":
				closeSuggestions();
				break;
		}
	});

	input.addEventListener("blur", () => {
		closeSuggestions();
	});
}

// Leaflet : Initialisation de la map city
async function initLeafletMap() {
	const mapArea = document.querySelector("#reseau-city");
	const mapAreaExtra1 = document.querySelector("#reseau-city-extra-1");
	const mapAreaExtra2 = document.querySelector("#reseau-city-extra-2");

	const urlParams = new URLSearchParams(window.location.search);
	let lat = urlParams.get("lat");
	let lon = urlParams.get("lon");

	// Page de taxonomie "département" : la carte est ciblée sur le département
	const department = mapArea ? mapArea.dataset.department : "";

	if (!lat) {
		if (department && mapArea.dataset.lat && mapArea.dataset.lon) {
			lat = mapArea.dataset.lat;
			lon = mapArea.dataset.lon;
		} else {
			lat = 48.8566;
			lon = 2.3522;
		}
	}

	if (mapArea) {
		// Initialisation de la map
		const leafletMap = L.map(mapArea, {
			center: [lat, lon],
			zoom: department ? 9 : 7,
			gestureHandling: true,
		});
		L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
			leafletMap
		);

		let leafletMapExtra1 = null;
		if (mapAreaExtra1) {
			leafletMapExtra1 = L.map(mapAreaExtra1, {
				center: [-21.1249, 55.5181],
				zoom: 7,
				gestureHandling: true,
			});
			L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
				leafletMapExtra1
			);
		}

		let leafletMapExtra2 = null;
		if (mapAreaExtra2) {
			leafletMapExtra2 = L.map(mapAreaExtra2, {
				center: [14.6612, -61.0206],
				zoom: 7,
				gestureHandling: true,
			});
			L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
				leafletMapExtra2
			);
		}

		const url = window.location.origin;
		const searchParams = new URLSearchParams(window.location.search);
		if (department) {
			searchParams.set("department", department);
		}
		const jsonURL = `${url}/shops-json/?ajax=1&${searchParams.toString()}`;

		const response = await fetch(jsonURL).then((data) => {
			return data.json();
		});

		response["shops"].forEach((shop) => {
			if (!shop) {
				return;
			}

			const marker = L.marker([shop["latitude"], shop["longitude"]], {
				icon: L.divIcon({
					html: `
						<i style="transform: translate(-40%, -60%); filter: drop-shadow(13px 15px 10px rgba(0, 0, 0, 0.33))">
							<svg xmlns="http://www.w3.org/2000/svg" height="50" viewBox="0 0 39.7 48.88">
								<path fill="var(--color-secondary)" d="M19.85 0C8.89 0 0 8.89 0 19.85c0 7.44 6.7 17.12 19.85 29.03C33 37.22 39.7 27.29 39.7 19.85 39.7 8.89 30.81 0 19.85 0Zm.34 32.88c-7.73 0-14-6.27-14-14s6.27-14 14-14 14 6.27 14 14-6.27 14-14 14Z"/>
								<path fill="var(--color-light)" d="M20.19 4.88c-7.73 0-14 6.27-14 14s6.27 14 14 14 14-6.27 14-14-6.27-14-14-14Zm0 26c-6.29 0-11.45-4.84-11.96-11h23.92c-.51 6.16-5.67 11-11.96 11Z"/>
								<path fill="var(--color-primary)" d="M32.15 19.88c-.51 6.16-5.67 11-11.96 11s-11.45-4.84-11.96-11h23.92Z"/>
							</svg>
						</i>
					`,
				}),
			});

			if (shop["localisation"]["code_postal"] < 97000 || !leafletMapExtra1 || !leafletMapExtra2) {
				leafletMap.addLayer(marker);
			} else if (
				shop["localisation"]["code_postal"] > 97000 &&
				shop["localisation"]["code_postal"] < 97400
			) {
				leafletMapExtra2.addLayer(marker);
			} else {
				leafletMapExtra1.addLayer(marker);
			}

			var popup = `
				<div class="pin">
					<div class="pin__title">
						${shop["title"]}
					</div>
					<div class="pin__address">
						${shop["localisation"]["adresse"]}
						${shop["localisation"]["code_postal"]}
						${shop["localisation"]["ville"]}
					</div>
					<div class="pin__phone">
						${shop["phone"]}
					</div>
					<a class="pin__link" href="${shop["link"]}">
						<span>Voir la fiche magasin</span>
					</a>
				</div>
			`;
			marker.bindPopup(popup);
		});

		// Recentrer sur la ville cherchée + les 3 magasins les plus proches (déjà triés par distance)
		if (response.city) {
			const points = [[response.city.lat, response.city.lon]].concat(
				response.shops.slice(0, 3).map((s) => [s.latitude, s.longitude])
			);
			leafletMap.fitBounds(points, { padding: [40, 40], maxZoom: 12 });
		}

		const geolocButtons = document.querySelectorAll(
			'[data-click="geolocalisation"]'
		);
		if (geolocButtons) {
			geolocButtons.forEach((button) => {
				button.addEventListener("click", () => {
					geoloc();
				});
			});
		}
	}
}
